/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useEffect, useRef, type Ref } from 'react';
import { type ColumnDef, type RowData } from '@tanstack/react-table';
import type { TableFeatureSet } from '../Table.features';

interface IndeterminateCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
  disableSelectAll?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export const IndeterminateCheckbox = ({
  ref,
  indeterminate,
  disableSelectAll,
  style = {},
  className,
  ...rest
}: IndeterminateCheckboxProps) => {
  const defaultRef = useRef<HTMLInputElement>(null);
  const resolvedRef = (ref || defaultRef) as React.MutableRefObject<HTMLInputElement | null>;

  if (disableSelectAll) {
    style.display = 'none';
  }

  useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = indeterminate ?? false;
    }
  }, [resolvedRef, indeterminate]);

  return (
    <input
      type="checkbox"
      className={className || 'ui-table-selector percentCenter'}
      ref={resolvedRef as any}
      style={style}
      {...rest}
    />
  );
};

const Selector = <T extends RowData>(
  disableSelectAll?: boolean,
  onSelectRow?: (selectedRows: any[], state?: any) => void
): ColumnDef<TableFeatureSet, T, unknown> => ({
  id: 'selection',
  minSize: 44,
  enableSorting: false,
  enableResizing: true,
  enableHiding: false,
  meta: {
    isVisible: true,
    className: 'ui__selector-column',
  },
  header: ({ table }) => {
    const { rows } = table.getRowModel();
    const isAllRowsSelected = table.getIsAllRowsSelected();
    const isSomeRowsSelected = table.getIsSomeRowsSelected();

    const handleToggleAll = () => {
      table.toggleAllRowsSelected();
      if (onSelectRow) {
        const newSelectedRows = isAllRowsSelected ? [] : rows;
        onSelectRow(newSelectedRows, table.store.state);
      }
    };

    return (
      <IndeterminateCheckbox
        checked={isAllRowsSelected}
        indeterminate={isSomeRowsSelected && !isAllRowsSelected}
        onChange={handleToggleAll}
        disableSelectAll={disableSelectAll}
        title="Toggle All Rows Selected"
      />
    );
  },
  cell: ({ row, table }) => {
    const { rows } = table.getRowModel();
    const selectedRows = table.getSelectedRowModel().rows;
    const isSelected = row.getIsSelected();

    const handleSelect = (e: React.MouseEvent) => {
      let rowsSelected = selectedRows;

      if (e.shiftKey) {
        // Shift select - select range
        if (!isSelected) {
          const originRow = selectedRows[0];
          if (originRow) {
            const originIndex = originRow.index;
            const currentIndex = row.index;
            const [startIndex, endIndex] = [originIndex, currentIndex].sort((a, b) => a - b);

            // Select all rows in range
            for (let i = startIndex; i <= endIndex; i++) {
              const targetRow = rows[i];
              if (targetRow && !targetRow.getIsSelected()) {
                targetRow.toggleSelected(true);
              }
            }

            rowsSelected = rows.slice(startIndex, endIndex + 1);
          }
        }
      } else {
        // Single select
        row.toggleSelected();

        // Update selected rows for callback
        if (isSelected) {
          // Row was unselected
          rowsSelected = selectedRows.filter((selectedRow) => selectedRow.id !== row.id);
        } else {
          // Row was selected
          rowsSelected = [...selectedRows, row];
        }
      }

      if (onSelectRow) {
        onSelectRow(rowsSelected, table.store.state);
      }
    };

    return (
      <IndeterminateCheckbox
        checked={isSelected}
        onChange={() => {}} // Handled by onClick
        onClick={handleSelect}
        title="Toggle Row Selected"
      />
    );
  },
});

export default Selector;
